import { useShallow } from "zustand/react/shallow";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { useUserStore } from "@/stores/userStore";
import { useTableParams } from "@components/organisms/pageTable";
import { collectionMapperFunc } from "@utils";
import type { ID } from "@constants";
import { IPaperType } from "@constants";
import type { IMemberPaperResponse } from "@api/members/papers";
import {
  useDeleteMemberPaper,
  useGetAllMemberPapers,
} from "@api/members/papers";
import { useCustomNotifyPromise } from "@hooks";
import { usePostMemberPaperApprove } from "@api/members/papers/approve";
import { usePostMemberPaperDisapprove } from "@api/members/papers/disapprove";
import type {
  IPaperDashboardViewProps,
  IPaperTabs,
} from "@/view/dashboard/types";
import { PageTabs, usePageTabParam } from "@components/atoms/pageTabs";
import { Container } from "@components/layout/container";
import type { IConferencePaperTable } from "@/template/articlesAndBooks/conferencePaper/table";
import { ConferencePaperTable } from "@/template/articlesAndBooks/conferencePaper/table";
import type { IJournalPaperTable } from "@/template/articlesAndBooks/journalPaper/table";
import { JournalPaperTable } from "@/template/articlesAndBooks/journalPaper/table";
import { journalPaperTableMapper } from "@pages/facultyMemberPanel/articlesAndBook/journalPaper/table/mapper";
import { conferencePaperTableMapper } from "@pages/facultyMemberPanel/articlesAndBook/conferencePaper/table/mapper";
import { Subtitle } from "@components/atoms/subtitle";

export function PaperDashboardView({ type }: IPaperDashboardViewProps) {
  const { t } = useTranslation("actions");
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const memberId = useUserStore(useShallow((state) => state.id));

  const notify = useCustomNotifyPromise();

  const { searchValue, paginationValues, tableProps, filterValues } =
    useTableParams({ index: type === IPaperType.journal ? 1 : 2 });

  const pageTabProps = usePageTabParam<IPaperTabs>(
    [
      { key: "approvedPapers", title: t("approvedPapers") },
      { key: "approvePendingPapers", title: t("approvePendingPapers") },
    ],
    type === IPaperType.journal ? 1 : 2,
    () => {
      tableProps.searchProps?.onChange?.("");
      tableProps.resetPage?.(params);
    },
  );

  const { data, isLoading, isFetching, isError } = useGetAllMemberPapers({
    variables: {
      memberId,
      type,
      search: searchValue,
      ...paginationValues,
      ...filterValues,
      status:
        pageTabProps?.selectedKey === "approvedPapers" ? "approved" : "pending",
    },
    select: (data) =>
      collectionMapperFunc(
        data,
        (type === IPaperType.conference
          ? conferencePaperTableMapper
          : journalPaperTableMapper) as (
          data: IMemberPaperResponse,
        ) => IConferencePaperTable,
      ),
  });

  const { mutateAsync: postMutateAsync } = useDeleteMemberPaper();

  const { mutateAsync: approve } = usePostMemberPaperApprove();
  const { mutateAsync: disapprove } = usePostMemberPaperDisapprove();

  async function deleteHandler(id: ID) {
    await postMutateAsync({ memberId, paperId: id });
  }

  const approveHandler = (id: ID) => {
    notify(approve({ paperId: id, memberId }));
  };
  const disapproveHandler = (id: ID) => {
    notify(disapprove({ paperId: id, memberId }));
  };

  return (
    <Container>
      <Subtitle
        title={
          type === IPaperType.conference
            ? "approveOrDisapproveConferencePapers"
            : "approveOrDisapproveJournalPapers"
        }
      />
      <PageTabs {...pageTabProps} />
      {type === IPaperType.conference ? (
        <ConferencePaperTable
          data={data?.items || []}
          isError={isError}
          isFetching={isFetching}
          isLoading={isLoading}
          onDelete={deleteHandler}
          total={data?.total_items || 0}
          tableParamProps={tableProps}
          onApprove={approveHandler}
          onReject={disapproveHandler}
          noAction
        />
      ) : (
        <JournalPaperTable
          data={(data?.items as unknown as IJournalPaperTable[]) || []}
          isError={isError}
          isFetching={isFetching}
          isLoading={isLoading}
          onDelete={deleteHandler}
          total={data?.total_items || 0}
          tableParamProps={tableProps}
          onApprove={approveHandler}
          onReject={disapproveHandler}
          noAction
        />
      )}
    </Container>
  );
}
